import { isCodingProblem } from 'platform-components/src/components/coding-problem/utils';
import { useKeyboardContext } from 'platform-components/src/components/keyboard/keyboard-context';
import { KEYBOARD_TYPES } from 'platform-components/src/components/keyboard/types';
import { getMe } from 'platform-components/src/components/user/selectors';
import { isWebProblem } from 'platform-components/src/components/web-problem/utils';
import { isWebserviceProblem } from 'platform-components/src/components/webservice-problem/utils';
import { useActions } from 'platform-components/src/utils/hooks/useActions';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMedia, useUnmount } from 'react-use';
import ym from 'react-yandex-metrika';

import * as problemViewSelectors from 'common.components/lesson-view.problem-view/selectors';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { ProblemRunBar } from 'common.components/problem-run/__bar/problem-run__bar';
import { PROBLEM_RUN_QUERY_MOBILE } from 'common.components/problem-run/constants';
import { actions as problemsViewActions } from 'store/problems-view/actions';
import * as problemsViewSelectors from 'store/problems-view/selectors';
import { isRobot } from 'store/problems/utils';
import { getTeamIdFromPath } from 'utils/path';

// eslint-disable-next-line complexity
const Component = () => {
    const isMobile = useMedia(PROBLEM_RUN_QUERY_MOBILE);

    const problemLink = useSelector(lessonViewSelectors.getSelectedProblemLink);
    const problem = problemLink?.problem;
    const isTheory = useSelector(lessonViewSelectors.getIsTheoryProblemSelected);
    const isOpenAnswerProblem = useSelector(lessonViewSelectors.getIsOpenAnswerProblemSelected);
    const shouldHideAnswerControls =
        isTheory ||
        isOpenAnswerProblem ||
        isRobot(problem || null) ||
        isWebProblem(problem) ||
        isWebserviceProblem(problem) ||
        isCodingProblem(problem);

    const answer = useSelector(problemsViewSelectors.getUserAnswer);
    const answerLoadingStatus = useSelector(problemsViewSelectors.getAnswerLoadingStatus);
    const isAnswered = useSelector(problemsViewSelectors.getIsAnswered);
    const isInitialAnswer = useSelector(problemViewSelectors.getIsInitialAnswer);
    const selectedProblemId = useSelector(problemsViewSelectors.getSelectedProblemId);
    const shouldShowCorrectAnswers = useSelector(problemsViewSelectors.getShouldShowCorrectAnswers);
    const { shouldShowKeyboard } = useKeyboardContext()[KEYBOARD_TYPES.PRIMARY];

    // для метрики
    const teamId = getTeamIdFromPath();
    const cLessonId = useSelector(lessonSelectors.getCLessonId);
    const lessonId = useSelector(lessonSelectors.getLessonId);
    const teacher = useSelector(getMe);
    const teacherId = teacher?.id || null;

    const { clearProblemAnswer, postAnswerRequest, toggleProblemCorrectAnswer } = useActions({
        clearProblemAnswer: problemsViewActions.clearProblemAnswer,
        postAnswerRequest: problemsViewActions.postAnswerRequest,
        toggleProblemCorrectAnswer: problemsViewActions.toggleProblemCorrectAnswer,
    });

    const toggleProblemCorrectAnswerWithMetrics = (shouldShowCorrectAnswersProp: boolean) => {
        toggleProblemCorrectAnswer(shouldShowCorrectAnswersProp);

        ym('reachGoal', 'teacher_card_view_show_right_answer', {
            cl_id: cLessonId,
            lesson_id: lessonId,
            location: cLessonId ? 'assign_window' : 'card_task',
            problem_id: selectedProblemId,
            teacher_id: teacherId,
            team_id: teamId,
            verdict: shouldShowCorrectAnswersProp,
        });
    };

    useUnmount(() => toggleProblemCorrectAnswerWithMetrics(false));

    const handleAnswer = useCallback(() => {
        if (selectedProblemId) {
            postAnswerRequest({ problemId: selectedProblemId, answer });

            ym('reachGoal', 'teacher_card_view_attempt', {
                cl_id: cLessonId,
                lesson_id: lessonId,
                location: cLessonId ? 'assign_window' : 'card_task',
                problem_id: selectedProblemId,
                teacher_id: teacherId,
                team_id: teamId,
            });
        }
    }, [selectedProblemId, answer, postAnswerRequest, cLessonId, lessonId, teacherId, teamId]);

    const handleRefresh = useCallback(() => {
        if (selectedProblemId) {
            clearProblemAnswer({ problemId: selectedProblemId });

            ym('reachGoal', 'teacher_card_view_refresh', {
                cl_id: cLessonId,
                lesson_id: lessonId,
                location: cLessonId ? 'assign_window' : 'card_task',
                problem_id: selectedProblemId,
                teacher_id: teacherId,
                team_id: teamId,
            });
        }
        toggleProblemCorrectAnswer(false);
    }, [selectedProblemId, clearProblemAnswer, toggleProblemCorrectAnswer, cLessonId, lessonId, teacherId, teamId]);

    if (shouldHideAnswerControls) {
        return null;
    }

    return (
        <ProblemRunBar
            answerLoadingStatus={answerLoadingStatus}
            disabled={!teacher?.is_teacher || isInitialAnswer || shouldShowCorrectAnswers || isAnswered}
            handleAnswer={handleAnswer}
            handleRefresh={handleRefresh}
            isAnswered={isAnswered}
            shouldShowAnswerButton={!isMobile}
            shouldShowCorrectAnswers={shouldShowCorrectAnswers}
            shouldShowProblemBar={!shouldShowKeyboard}
            toggleProblemCorrectAnswer={toggleProblemCorrectAnswerWithMetrics}
        />
    );
};
// eslint-enable-next-line complexity

export const ProblemViewProblemBarContainer = React.memo(Component);
